/**
 * @fileoverview Hook for tracking referral clicks and user/wallet attribution
 * @module features/referrals/hooks/useReferralTracking
 *
 * Implements complete referral tracking flow with user-based attribution (PREFERRED):
 * 1. Detects 'ref' parameter in URL
 * 2. Saves to localStorage immediately
 * 3. When user logs in, migrates to user_referral_attributions (PREFERRED)
 * 4. Falls back to wallet_referral_attributions if user not logged in
 * 5. Auto-migrates existing wallet attributions to user when user logs in
 * 6. Clears localStorage after migration
 *
 * Attribution Model: LAST-CLICK ATTRIBUTION
 * - If user clicks KOL A link, then KOL B link, KOL B gets the commission
 * - Each new referral click overwrites the previous attribution
 * - Attribution is preserved even if user returns without clicking a referral link
 *
 * Priority: User Attribution > Wallet Attribution > localStorage
 *
 * Usage: Add to root layout or deal pages where referral tracking needed.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from '@tanstack/react-router';
import { useAccount } from 'wagmi';
import { useEnhancedAuth } from '@/lib/auth';
import {
  saveReferral,
  getStoredReferral,
  clearReferral,
} from '../utils/referralStorage';
import {
  getKOLBySlug,
  saveWalletAttribution,
  getWalletAttribution,
  saveUserAttribution,
  getUserAttribution,
  migrateWalletToUserAttribution,
} from '../services/referralService';

/**
 * Hook for automatic referral tracking with user-based attribution.
 *
 * Handles:
 * - URL parameter detection ('ref')
 * - localStorage persistence
 * - User attribution (PREFERRED) - cross-device, multi-wallet support
 * - Wallet attribution (FALLBACK) - for non-logged-in users
 * - Automatic migration from wallet → user on login
 * - Duplicate prevention
 *
 * @example
 * ```typescript
 * // In App.tsx or layout component
 * function App() {
 *   useReferralTracking();
 *
 *   return <Routes />;
 * }
 * ```
 *
 * @example
 * ```typescript
 * // User journey (PREFERRED - User-based with LAST-CLICK):
 * // 1. Visits: /deals/project?ref=crypto_expert
 * //    → Saves to localStorage
 * // 2. Logs in with Google/Twitter
 * //    → Migrates to user_referral_attributions (crypto_expert gets commission)
 * //    → Clears localStorage
 * // 3. Visits: /deals/project?ref=blockchain_guru (WHILE LOGGED IN)
 * //    → Saves to localStorage
 * //    → Overwrites attribution (blockchain_guru gets commission now)
 * //    → Clears localStorage
 * // 4. Returns to site without referral link
 * //    → Still attributed to blockchain_guru (last click wins)
 * // 5. Visits from different device, logs in with same account
 * //    → Attribution still works (blockchain_guru, cross-device)
 * ```
 *
 * @example
 * ```typescript
 * // User journey (FALLBACK - Wallet-based):
 * // 1. Visits: /deals/project?ref=crypto_expert
 * //    → Saves to localStorage
 * // 2. Connects wallet WITHOUT logging in: 0x1234...5678
 * //    → Migrates to wallet_referral_attributions (fallback)
 * //    → Clears localStorage
 * // 3. Later logs in with Google/Twitter
 * //    → Auto-migrates to user_referral_attributions
 * //    → Now uses user attribution (cross-device support enabled)
 * ```
 */
export function useReferralTracking(): void {
  const location = useLocation();
  const { address, isConnected } = useAccount();
  const { user } = useEnhancedAuth();

  // Track if we've already migrated for this user/wallet to prevent duplicates
  const hasUserMigratedRef = useRef(false);
  const hasWalletMigratedRef = useRef(false);

  // Effect 1: Detect and save referral from URL parameter
  useEffect(() => {
    // Check for 'ref' parameter in URL
    const searchParams = new URLSearchParams(location.search);
    const refParam = searchParams.get('ref');

    if (refParam) {

      // Save to localStorage immediately
      saveReferral(refParam, 'link');
    }
  }, [location.search]);

  // Effect 2: Migrate to database when user logs in (PREFERRED)
  useEffect(() => {
    // Only run if user is authenticated and we haven't migrated yet
    if (!user?.id || hasUserMigratedRef.current) {
      return;
    }

    const migrateUserAttribution = async (): Promise<void> => {
      try {

        // Get referral from localStorage FIRST (for last-click attribution)
        const storedReferral = getStoredReferral();

        // Check if user already has attribution
        const existingAttribution = await getUserAttribution(user.id);

        // If no new referral in localStorage
        if (!storedReferral) {
          if (existingAttribution) {
            hasUserMigratedRef.current = true;
            return;
          }

          // No localStorage referral, but check if we should migrate from wallet
          if (isConnected && address) {
            const migrated = await migrateWalletToUserAttribution(user.id, address);
            if (migrated) {
            } else {
            }
          }
          hasUserMigratedRef.current = true;
          return;
        }

        // If new referral in localStorage, check if it's different from existing
        if (existingAttribution && existingAttribution.kol_slug === storedReferral.kolSlug) {
          // Clear localStorage since it matches database
          clearReferral();
          hasUserMigratedRef.current = true;
          return;
        }

        // Get KOL data from slug
        const kol = await getKOLBySlug(storedReferral.kolSlug);

        if (!kol) {
          clearReferral(); // Clear invalid referral
          hasUserMigratedRef.current = true;
          return;
        }


        // Save user attribution (PREFERRED METHOD)
        await saveUserAttribution({
          user_id: user.id,
          kol_id: kol.id,
          kol_slug: kol.slug,
          referral_source: storedReferral.source,
        });


        // Clear localStorage now that it's in database
        clearReferral();
        hasUserMigratedRef.current = true;

        // Also migrate wallet attribution if wallet is connected
        if (isConnected && address) {
          await migrateWalletToUserAttribution(user.id, address);
        }
      } catch (error) {
        // Don't clear localStorage on error - will retry next time
        console.error('[Referral] Migration failed:', error);
        hasUserMigratedRef.current = true;
      }
    };

    migrateUserAttribution();
  }, [user?.id, isConnected, address]);

  // Effect 3: Fallback - Migrate to wallet attribution if user not logged in
  useEffect(() => {
    // Only run if wallet is connected, user NOT logged in, and we haven't migrated yet
    if (!isConnected || !address || user?.id || hasWalletMigratedRef.current) {
      return;
    }

    const migrateToWalletDatabase = async (): Promise<void> => {
      try {

        // Get referral from localStorage
        const storedReferral = getStoredReferral();

        if (!storedReferral) {
          hasWalletMigratedRef.current = true;
          return;
        }


        // Get KOL data from slug
        const kol = await getKOLBySlug(storedReferral.kolSlug);

        if (!kol) {
          clearReferral(); // Clear invalid referral
          hasWalletMigratedRef.current = true;
          return;
        }


        // Check if wallet already has attribution in database
        const existingAttribution = await getWalletAttribution(address);

        if (existingAttribution && existingAttribution.kol_slug === storedReferral.kolSlug) {
          // Clear localStorage since database already has this attribution
          clearReferral();
          hasWalletMigratedRef.current = true;
          return;
        }

        // Save wallet attribution (FALLBACK METHOD)
        await saveWalletAttribution({
          wallet_address: address,
          kol_id: kol.id,
          kol_slug: kol.slug,
          user_id: undefined, // No user logged in
          referral_source: storedReferral.source,
        });


        // Clear localStorage now that it's in database
        clearReferral();
        hasWalletMigratedRef.current = true;
      } catch (error) {
        // Don't clear localStorage on error - will retry next time
        console.error('[Referral] Migration failed:', error);
        hasWalletMigratedRef.current = true;
      }
    };

    migrateToWalletDatabase();
  }, [address, isConnected, user?.id]);

  // Reset migration flags when wallet disconnects
  useEffect(() => {
    if (!isConnected) {
      hasUserMigratedRef.current = false;
      hasWalletMigratedRef.current = false;
    }
  }, [isConnected]);
}

/**
 * Hook to get current referral attribution for connected wallet.
 *
 * Returns the KOL slug that should receive commission for this wallet's investments.
 * Checks database first, then falls back to localStorage.
 *
 * @returns KOL slug or null
 *
 * @example
 * ```typescript
 * function InvestmentButton() {
 *   const referralSlug = useReferralAttribution();
 *
 *   const handleInvest = async () => {
 *     let referrerAddress = '0x0000000000000000000000000000000000000000';
 *
 *     if (referralSlug) {
 *       const kol = await getKOLBySlug(referralSlug);
 *       if (kol?.wallet_address) {
 *         referrerAddress = kol.wallet_address;
 *       }
 *     }
 *
 *     await invest(amount, referrerAddress);
 *   };
 * }
 * ```
 */
export function useReferralAttribution(): string | null {
  // This would need to be implemented with TanStack Query for proper caching
  // For now, returning null - will be used in useInvestment hook
  // The actual logic will be in getReferrerAddress function in useInvestment

  return null;
}
